import React, { useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import ReportWidget from './components/ReportWidget';
import { fetchInventoryReport } from '../../store/reportingInventory/actions';

const TOP_N = 5;

const MostConsumedPartsWidget = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    inventoryReport: data,
    inventoryReportLoading: loading,
    inventoryReportError: error,
  } = useSelector((state) => state.ReportingInventory);

  useEffect(() => {
    if (!data && !loading && !error) dispatch(fetchInventoryReport());
  }, [dispatch, data, loading, error]);

  const handleRefresh = useCallback(() => {
    dispatch(fetchInventoryReport());
  }, [dispatch]);

  const chartData = useMemo(() => {
    const rows = data?.mostFrequentlyConsumedParts || [];
    return rows.slice(0, TOP_N).map((row) => ({
      name: row.partName,
      quantity: Number(row.totalQuantityConsumed) || 0,
      timesConsumed: Number(row.timesConsumed) || 0,
    }));
  }, [data]);

  const isEmpty = chartData.length === 0;

  return (
    <ReportWidget
      title={t('business.inventory.most_frequently_consumed_parts', 'Most Frequently Consumed Parts')}
      loading={loading}
      error={error}
      isEmpty={isEmpty}
      emptyMessage={t('reporting.most_consumed_parts.empty', 'No spare-part consumption to display.')}
      onRefresh={handleRefresh}
    >
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={chartData} margin={{ top: 5, right: 30, left: 10, bottom: 20 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" height={100} />
          <YAxis tick={{ fontSize: 12 }} />
          <Tooltip
            contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: 8,
              padding: 12,
            }}
          />
          <Legend />
          <Bar
            dataKey="quantity"
            fill="#10b981"
            name={t('shared.total_quantity', 'Total Quantity')}
            radius={[4, 4, 0, 0]}
          />
          <Bar
            dataKey="timesConsumed"
            fill="#3b82f6"
            name={t('business.inventory.times_used', 'Times Used')}
            radius={[4, 4, 0, 0]}
          />
        </BarChart>
      </ResponsiveContainer>
    </ReportWidget>
  );
};

export default MostConsumedPartsWidget;
